import React, { useState, useEffect, useMemo } from "react";
import styled from "styled-components";
import axios from "axios";
import { useNavContext } from "../context/nav_context";
import { Loading, UserItem } from "../components";
import { api_v3_list_of_players, api_v3_list_of_clans } from "../utils/constants";

const AdminUsers = () => {
  const { user } = useNavContext();
  const [users, setUsers] = useState([]);
  const [clans, setClans] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [clansLoading] = useState(false);
  const [clansError] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetch = async () => {
      setIsLoading(true);
      try {
        const [usersRes, clansRes] = await Promise.all([
          axios.get(api_v3_list_of_players()),
          axios.get(api_v3_list_of_clans()),
        ]);
        if (usersRes.status === 200) {
          const sorted = usersRes.data.sort((a, b) =>
            (a.username || "").localeCompare(b.username || "")
          );
          setUsers(sorted);
        }
        if (clansRes.status === 200) {
          setClans(clansRes.data.sort((a, b) => (a.name > b.name ? 1 : -1)));
        }
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!search.trim()) return users;
    const q = search.toLowerCase();
    return users.filter(
      (u) =>
        (u.username && u.username.toLowerCase().includes(q)) ||
        (u.discordUsername && u.discordUsername.toLowerCase().includes(q)) ||
        (u.discordId && u.discordId.toLowerCase().includes(q)) ||
        (u.playerId && u.playerId.toLowerCase().includes(q))
    );
  }, [users, search]);

  if (!user) {
    return (
      <Canvas>
        <EmptyState>Debes iniciar sesión para acceder a este perfil.</EmptyState>
      </Canvas>
    );
  }

  if (!user.isSuperAdmin) {
    return (
      <Canvas>
        <EmptyState>No tienes permisos para acceder a esta sección.</EmptyState>
      </Canvas>
    );
  }

  return (
    <Canvas>
      <Content>
        <PageTitle>GESTIÓN DE USUARIOS</PageTitle>

        <HeaderBar>
          <PlayerCount>
            {filteredUsers.length}
            <PlayerCountLabel>JUGADORES</PlayerCountLabel>
          </PlayerCount>
          <SearchInput
            placeholder="Buscar por username, discord ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </HeaderBar>

        {isLoading ? (
          <Loading />
        ) : (
          <ListContainer>
            {filteredUsers.map((u) => (
              <UserItem
                key={u.playerId}
                user={u}
                clans={clans}
                clansInProgress={clansLoading}
                clansError={clansError}
              />
            ))}
            {filteredUsers.length === 0 && (
              <EmptyState>No se encontraron usuarios</EmptyState>
            )}
          </ListContainer>
        )}
      </Content>
    </Canvas>
  );
};

const Canvas = styled.div`
  background: var(--v-canvas);
  min-height: 100vh;
  padding: 4rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Content = styled.div`
  width: 100%;
  max-width: 1100px;
`;

const PageTitle = styled.h1`
  font-family: Anton, sans-serif;
  font-size: clamp(48px, 8vw, 72px);
  font-weight: 400;
  text-transform: uppercase;
  line-height: 1;
  color: var(--v-fg);
  margin: 0 0 2rem;
`;

const EmptyState = styled.p`
  font-family: var(--v-font-body);
  font-size: 14px;
  color: var(--v-fg-muted);
  text-align: center;
  padding: 4rem 1rem;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const PlayerCount = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  font-family: var(--v-font-body);
  font-size: 32px;
  font-weight: 700;
  color: var(--v-fg);
  flex-shrink: 0;
`;

const PlayerCountLabel = styled.span`
  font-family: var(--v-font-mono);
  font-size: 10px;
  font-weight: 600;
  color: var(--v-fg-muted);
  letter-spacing: 1px;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.6rem 1rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  color: var(--v-fg);
  font-family: var(--v-font-body);
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease;
  &:focus { border-color: var(--v-mint); }
  &::placeholder { color: var(--v-fg-muted); opacity: 0.5; }
`;

const ListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export default AdminUsers;
